// Part groups --- Release for all…: one change notice releasing every part of a group whose spec changed (after a shared
// dieline went to all of them, say). change_notice.release is all or none and needs each spec's stamp, so the dialog
// works out each part first and leaves out the ones it can't release: no spec, nothing changed, or a file slot in the
// draft with nothing uploaded. Each part still gets its own next revision letter.
import { Rocket } from "lucide-react";
import { useState } from "react";
import type { Item, PartGroup } from "../../../shared/db.gen.ts";
import { memberLabel } from "../../../shared/part-groups.ts";
import { SPEC_FILE_KIND_LABELS } from "../../../shared/specs.ts";
import { plural } from "../lib/format.ts";
import { mutate, mutateWithResult } from "../lib/mutate.ts";
import { Link } from "../lib/router.tsx";
import { Badge, Button, Input, Textarea } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";
import { Modal } from "./PartGroups.tsx";
import { type ReleasePreview, releasePreview, useSpecIndex } from "./specs/lib.ts";

type Row = { item: Item; preview: ReleasePreview | null; state: "releases" | "unchanged" | "no_spec" | "blocked" };

/** What each part's release would be, as it is now (the dialog keeps this, and the server checks each stamp). */
function plan(ix: ReturnType<typeof useSpecIndex>, members: readonly Item[]): Row[] {
  return members.map((item) => {
    const spec = ix.specByItem.get(item.id);
    const preview = spec ? releasePreview(ix, spec) : null;
    const state = !preview ? "no_spec" : !preview.pending.any || preview.pending.empty ? "unchanged" : preview.missing.length ? "blocked" : "releases";
    return { item, preview, state };
  });
}

/** "new dieline and print artwork", from the files the releases change. */
function whatChanged(rows: Row[]): string {
  const kinds = new Set<string>();
  let text = false;
  for (const r of rows) {
    if (r.state !== "releases" || !r.preview) continue;
    const f = r.preview.pending.files;
    for (const x of [...f.added, ...f.changed.map((c) => c.to)]) kinds.add(SPEC_FILE_KIND_LABELS[x.kind].toLowerCase());
    if (r.preview.pending.text || r.preview.pending.zh) text = true;
  }
  const list = [...kinds];
  if (!list.length) return text ? "spec text" : "update";
  return `new ${list.length > 1 ? `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}` : list[0]}${text ? ", spec text" : ""}`;
}

export function ReleaseAllDialog({ group, members, onClose }: { group: PartGroup; members: Item[]; onClose: () => void }) {
  const ix = useSpecIndex();
  const [rows] = useState(() => plan(ix, members));
  const releasing = rows.filter((r) => r.state === "releases");
  const [title, setTitle] = useState(`${group.name}: ${whatChanged(rows)}`);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy || !releasing.length) return;
    if (!body.trim()) return toast.error("Say what changed and why");
    setBusy(true);
    const id = crypto.randomUUID();
    let ok = await mutate("change_notice.create", { id, title: title.trim() || group.name, body_md: body, effective: "next_po" });
    for (const r of releasing) ok = ok && (await mutate("change_notice_item.create", { id: crypto.randomUUID(), change_notice_id: id, item_id: r.item.id }));
    const expect = Object.fromEntries(releasing.map((r) => [r.item.id, r.preview?.stamp ?? null]));
    const released = ok ? await mutateWithResult("change_notice.release", { id, expect }) : { ok: false };
    setBusy(false);
    if (!released.ok) {
      if (ok) toast.info("The change notice is saved as a draft: open it under Specs → Change notices");
      return;
    }
    const r = released.result as { number: string; released: { sku: string; rev: string }[] };
    toast.ok(`Released ${r.number}: ${plural(r.released.length, "part")} of ${group.name}`);
    onClose();
  };

  const tone = { releases: "warn", unchanged: "neutral", no_spec: "neutral", blocked: "danger" } as const;
  const said = (r: Row) =>
    r.state === "releases"
      ? `Releases Rev ${r.preview?.rev}`
      : r.state === "blocked"
        ? `Left out: “${r.preview?.missing[0]}” has nothing uploaded`
        : r.state === "no_spec"
          ? "Left out: no spec"
          : `Left out: nothing changed${r.preview?.current ? ` since Rev ${r.preview.current}` : ""}`;

  return (
    <Modal
      title={`Release ${group.name}`}
      width={600}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Link to="/specs?tab=ecn" className="mr-auto text-[12px] text-accent-text hover:underline">
            Change notices
          </Link>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" icon={<Rocket className="size-3.5" />} disabled={busy || !releasing.length} kbd="↵">
            Release {plural(releasing.length, "part")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="text-[12px] text-muted">
          One change notice, from the next PO: each part below whose spec changed gets its next revision, linked to it. Vendors get them with their next PO, or
          with Send update on one already sent.
        </div>
        <div className="max-h-[36vh] overflow-y-auto rounded-md bg-surface-2 px-3 py-2 text-[12px]">
          {rows.map((r) => (
            <div key={r.item.id} className="flex items-center gap-2 py-0.5">
              <span className="w-32 shrink-0 truncate font-mono">{r.item.sku}</span>
              <span className="w-44 shrink-0 truncate text-muted">{memberLabel(group, r.item)}</span>
              <Badge tone={tone[r.state]}>{said(r)}</Badge>
            </div>
          ))}
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">Title</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-muted">What changed and why (vendors see it)</span>
          <Textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="e.g. New die cut: the tuck flap is 2 mm longer." />
        </label>
        {!releasing.length && <div className="text-[12px] text-danger">Nothing to release: no part's spec has changed since its last revision.</div>}
      </div>
    </Modal>
  );
}
